import { useState, useEffect } from 'react';
import { getSubjectsForUser, type Subject } from '../data/syllabus';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';

export interface StudentProfile {
  sh_group: 'science' | 'humanities' | 'commerce';
  sh_optional: string | null;
  target_batch: string;
  enrolled_courses?: number[];
  stats?: {
    points: number;
    rank: number | string;
    exams: number;
    streak: number;
  };
}

export interface ShuddhoshikhaContextType {
  profile: StudentProfile | null;
  subjects: Subject[];
  isLoading: boolean;
  error: string | null;
  availableQuestions?: {
    chapterWise: number;
    mockTests: number;
    questionBanks: number;
    schoolQuestionBanks: number;
  };
}

const idMap: Record<string, string> = {
  'bangla': 'sub_bn1',
  'bangla-1st': 'sub_bn1',
  'bangla-2nd': 'sub_bn2',
  'english': 'sub_eng1',
  'english-1st': 'sub_eng1',
  'english-2nd': 'sub_eng2',
  'general-math': 'sub_math',
  'ict': 'sub_ict',
  'religion': 'sub_religion',
  'physics': 'sub_phy',
  'chemistry': 'sub_chem',
  'biology': 'sub_bio',
  'higher-math': 'sub_hmath',
  'history': 'sub_hist',
  'economics': 'sub_eco',
  'geography': 'sub_geo',
  'civics': 'sub_civ',
  'general-science': 'sub_gsci',
  'accounting': 'sub_acc',
  'finance': 'sub_fin',
  'business': 'sub_bus',
  'agriculture': 'opt_agri',
  'home-science': 'opt_home',
};

const getDbChapterId = (chapId: string): string => {
  if (chapId.startsWith('b1_ch')) return chapId.replace('b1_ch', 'bangla-ch');
  if (chapId.startsWith('b2_ch')) return chapId.replace('b2_ch', 'bangla-2nd-ch');
  if (chapId.startsWith('e1_ch')) return chapId.replace('e1_ch', 'english-ch');
  if (chapId.startsWith('e2_ch')) return chapId.replace('e2_ch', 'english-2nd-ch');
  if (chapId.startsWith('m_ch')) return chapId.replace('m_ch', 'general-math-ch');
  if (chapId.startsWith('ict_ch')) return chapId.replace('ict_ch', 'ict-ch');
  if (chapId.startsWith('rel_ch')) return chapId.replace('rel_ch', 'religion-ch');
  if (chapId.startsWith('phy_ch')) return chapId.replace('phy_ch', 'phys-ch');
  if (chapId.startsWith('chem_ch')) return chapId.replace('chem_ch', 'chem-ch');
  if (chapId.startsWith('bio_ch')) return chapId.replace('bio_ch', 'biology-ch');
  if (chapId.startsWith('hm_ch')) return chapId.replace('hm_ch', 'hmath-ch');
  if (chapId.startsWith('hist_ch')) return chapId.replace('hist_ch', 'history-ch');
  if (chapId.startsWith('geo_ch')) return chapId.replace('geo_ch', 'geography-ch');
  if (chapId.startsWith('civ_ch')) return chapId.replace('civ_ch', 'civics-ch');
  if (chapId.startsWith('gs_ch')) return chapId.replace('gs_ch', 'gen-science-ch');
  if (chapId.startsWith('acc_ch')) return chapId.replace('acc_ch', 'accounting-ch');
  if (chapId.startsWith('fin_ch')) return chapId.replace('fin_ch', 'finance-ch');
  if (chapId.startsWith('bus_ch')) return chapId.replace('bus_ch', 'business-ch');
  if (chapId.startsWith('agr_ch')) return chapId.replace('agr_ch', 'agriculture-ch');
  if (chapId.startsWith('hs_ch')) return chapId.replace('hs_ch', 'home-science-ch');
  return chapId;
};

export const useShuddhoshikhaContext = () => {
  const [userContext, setUserContext] = useState<ShuddhoshikhaContextType>({
    profile: null,
    subjects: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const loadAllData = async () => {
      const shAuth = (window as any).shAuth;
      const nonce = shAuth?.rest_nonce || shAuth?.nonce || (window as any).chorcaghorSettings?.nonce || '';
      
      let profileData: StudentProfile | null = null;
      let subjectsData: Subject[] = [];
      let fetchError: string | null = null;

      // Try to load from shAuth localized user state first as direct source of truth
      if (shAuth && shAuth.user) {
        profileData = {
          sh_group: shAuth.user.group || 'science',
          sh_optional: shAuth.user.optional || null,
          target_batch: shAuth.user.class ? (shAuth.user.class.includes('2026') ? 'hsc_2026' : 'ssc_2027') : 'ssc_2027',
          enrolled_courses: shAuth.user.enrolled_courses || [],
          stats: shAuth.user.stats || {
            points: 0,
            rank: '...',
            exams: 9,
            streak: 2
          }
        };
      }

      let availableQuestionsData = shAuth?.user?.available_questions;

      // 1. Fetch profile context from backend REST API (only if not preloaded)
      if (!availableQuestionsData) {
        try {
          const response = await fetch('/wp-json/shuddhoshikha/v1/user-context', {
            headers: {
              'X-WP-Nonce': nonce,
            },
          });
          if (response.ok) {
            const res = await response.json();
            if (res.success) {
              if (res.profile) {
                profileData = {
                  sh_group: res.profile.sh_group,
                  sh_optional: res.profile.sh_optional,
                  target_batch: res.profile.target_batch,
                  enrolled_courses: res.profile.enrolled_courses || [],
                  stats: res.profile.stats || {
                    points: 0,
                    rank: '...',
                    exams: 9,
                    streak: 2
                  }
                };
              }
              if (res.available_questions) {
                availableQuestionsData = res.available_questions;
              }
            }
          }
        } catch (err: any) {
          fetchError = err.message;
        }
      }

      // No fallback — if backend is unavailable, profile stays null
      // and the skeleton UI continues rendering
      
      const hardcodedSubjects = profileData 
        ? getSubjectsForUser(profileData.sh_group, profileData.sh_optional)
        : [];

      // Try to load subjects from preloaded window state
      if (shAuth?.user?.subjects && Array.isArray(shAuth.user.subjects) && profileData) {
        subjectsData = shAuth.user.subjects.map((sub: any) => {
          const frontendId = idMap[sub.id] || sub.id;
          const hardcodedSub = hardcodedSubjects.find(s => s.id === frontendId);
          // Normalize chapters: backend sends {id, name, totalQuestions, is_paid, is_locked}
          const chapters = (sub.chapters || []).map((ch: any) => {
            const hardcodedChap = hardcodedSub?.chapters.find(c => {
              return c.id === ch.id || getDbChapterId(c.id) === ch.id;
            });
            return {
              id: ch.id,
              name: ch.name || ch.id,
              totalQuestions: ch.totalQuestions || 0,
              tab: ch.tab || hardcodedChap?.tab,
              is_paid: !!ch.is_paid,
              is_locked: !!ch.is_locked,
            };
          });
          return {
            ...sub,
            id: frontendId,
            db_slug: sub.id,
            icon: sub.icon || 'BookOpen',
            chapters,
          };
        });
      }

      // 2. Fetch student syllabus configuration (fallback if not preloaded)
      if (subjectsData.length === 0 && shAuth && shAuth.ajax_url && nonce && profileData) {
        try {
          const formData = new FormData();
          formData.append('action', 'chorcha_get_student_syllabus');
          formData.append('nonce', nonce);

          const response = await fetch(normalizeAjaxUrl(shAuth.ajax_url), {
            method: 'POST',
            body: formData,
          });
          if (response.ok) {
            const res = await response.json();
            if (res.success && res.data.subjects) {
              // Normalize backend DB IDs to frontend IDs
              subjectsData = res.data.subjects.map((sub: any) => {
                const frontendId = idMap[sub.id] || sub.id;
                const hardcodedSub = hardcodedSubjects.find(s => s.id === frontendId);
                // Normalize chapters: backend sends {id, name, totalQuestions, is_paid, is_locked}
                const chapters = (sub.chapters || []).map((ch: any) => {
                  const hardcodedChap = hardcodedSub?.chapters.find(c => {
                    return c.id === ch.id || getDbChapterId(c.id) === ch.id;
                  });
                  return {
                    id: ch.id,
                    name: ch.name || ch.id,
                    totalQuestions: ch.totalQuestions || 0,
                    tab: ch.tab || hardcodedChap?.tab,
                    is_paid: !!ch.is_paid,
                    is_locked: !!ch.is_locked,
                  };
                });
                return {
                  ...sub,
                  id: frontendId,
                  db_slug: sub.id,
                  icon: sub.icon || 'BookOpen',
                  chapters,
                };
              });
            }
          }
        } catch (err) {
          // ignore, fall back below
        }
      }

      // No fallback — show actual counts or default to 0
      if (!availableQuestionsData) {
        availableQuestionsData = {
          chapterWise: 0,
          mockTests: 0,
          questionBanks: 0,
          schoolQuestionBanks: 0
        };
      }

      // Fallback guest/dev profile if not logged in / standalone mode
      if (!profileData) {
        profileData = {
          sh_group: 'science',
          sh_optional: 'higher-math',
          target_batch: 'ssc_2027',
          stats: {
            points: 1250,
            rank: 5,
            exams: 12,
            streak: 3
          }
        };
      }

      // No fallback — empty subjects renders "No subjects assigned" UI
      if (subjectsData.length === 0 && profileData) {
        subjectsData = getSubjectsForUser(profileData.sh_group, profileData.sh_optional);
      }

      setUserContext({
        profile: profileData,
        subjects: subjectsData,
        isLoading: false,
        error: fetchError,
        availableQuestions: availableQuestionsData,
      });
    };

    loadAllData();
  }, []);

  return userContext;
};
